/**
 * Custom Multicolumn Section Slider - BEM JavaScript
 * True carousel slider with transform translateX
 */

class MulticolumnSlider {
  constructor(sectionId) {
    // DOM Elements - scoped to specific section instance
    this.section = document.querySelector(`#multicolumn-section-${sectionId}`);
    if (!this.section) return;

    this.slidesContainer = this.section.querySelector(
      ".multicolumn-section__slides"
    );
    this.slides = Array.from(
      this.section.querySelectorAll(".multicolumn-section__slide")
    );
    this.prevButton = this.section.querySelector(
      ".multicolumn-section__nav-button--prev"
    );
    this.nextButton = this.section.querySelector(
      ".multicolumn-section__nav-button--next"
    );
    this.counterCurrent = this.section.querySelector(
      ".multicolumn-section__counter-current"
    );
    this.counterTotal = this.section.querySelector(
      ".multicolumn-section__counter-total"
    );

    // State
    this.currentIndex = 0;
    this.totalSlides = this.slides.length;
    this.viewportMode = this.getViewportMode();
    this.isAnimating = false;

    // Initialize
    this.init();
  }

  init() {
    if (!this.slidesContainer || this.totalSlides === 0) return;

    // Event Listeners
    this.prevButton.addEventListener("click", () => this.navigate("prev"));
    this.nextButton.addEventListener("click", () => this.navigate("next"));

    // Responsive handling
    window.addEventListener("resize", () => this.handleResize());

    // Initial setup
    this.updateView();
  }

  getViewportMode() {
    const width = window.innerWidth;
    if (width > 1024) return "desktop";
    if (width > 640) return "tablet";
    return "mobile";
  }

  handleResize() {
    const newMode = this.getViewportMode();

    // Reset if viewport mode changes
    if (newMode !== this.viewportMode) {
      this.viewportMode = newMode;
      this.currentIndex = 0;
      this.slidesContainer.style.transform = "translateX(0)";
      this.updateView();
    }
  }

  navigate(direction) {
    const mode = this.viewportMode;

    if (mode === "desktop" || this.isAnimating) return;

    this.isAnimating = true;

    if (direction === "prev") {
      this.currentIndex = Math.max(0, this.currentIndex - 1);
    } else {
      if (mode === "tablet") {
        // Tablet: max index depends on total slides
        const maxIndex = Math.max(0, this.totalSlides - 2);
        this.currentIndex = Math.min(maxIndex, this.currentIndex + 1);
      } else {
        // Mobile: max index is totalSlides - 1
        this.currentIndex = Math.min(
          this.totalSlides - 1,
          this.currentIndex + 1
        );
      }
    }

    this.updateCarousel();

    // Reset animation lock after transition
    setTimeout(() => {
      this.isAnimating = false;
    }, 500);
  }

  updateCarousel() {
    const mode = this.viewportMode;
    let translateValue = 0;

    if (mode === "tablet") {
      // Tablet: 2 slides visible, each slide is calc(50% - 12px)
      // Gap is 24px
      const containerWidth = this.slidesContainer.offsetWidth;
      const gap = 24;
      // Each slide width = calc(50% - 12px) = containerWidth / 2 - 12
      const slideWidth = containerWidth / 2 - 12;
      // Move by slideWidth + gap to show next pair
      translateValue = -(this.currentIndex * (slideWidth + gap));
    } else if (mode === "mobile") {
      // Mobile: 1 slide visible, each slide is 100% width
      const containerWidth = this.slidesContainer.offsetWidth;
      const gap = 20;
      // Each slide move = 100% of container + gap
      translateValue = -(this.currentIndex * (containerWidth + gap));
    }

    this.slidesContainer.style.transform = `translateX(${translateValue}px)`;
    this.updateView();
  }

  updateView() {
    const mode = this.viewportMode;

    if (mode === "desktop") {
      // Desktop: Show current position
      this.updateCounter(1, Math.ceil(this.totalSlides / 3) || 1);
      this.slidesContainer.style.transform = "translateX(0)";
    } else if (mode === "tablet") {
      // Tablet: Show as pairs (1/2, 2/2 for 3 slides, or 1/2 for 2 slides)
      const totalPages = Math.ceil(this.totalSlides / 2);
      this.updateCounter(this.currentIndex + 1, totalPages);
      this.updateButtons();
    } else {
      // Mobile: Show individual slides
      this.updateCounter(this.currentIndex + 1, this.totalSlides);
      this.updateButtons();
    }
  }

  updateCounter(position, total) {
    this.counterCurrent.textContent = position;
    this.counterTotal.textContent = total;
  }

  updateButtons() {
    const mode = this.viewportMode;

    if (mode === "desktop") {
      return;
    }

    // Disable prev button if at start
    this.prevButton.disabled = this.currentIndex === 0;

    // Disable next button if at end
    if (mode === "tablet") {
      // Tablet: max index depends on total slides
      const maxIndex = Math.max(0, this.totalSlides - 2);
      this.nextButton.disabled = this.currentIndex === maxIndex;
    } else {
      // Mobile: max index is totalSlides - 1
      this.nextButton.disabled = this.currentIndex === this.totalSlides - 1;
    }
  }
}

// Initialize all multicolumn sliders on the page
function initMulticolumnSliders() {
  const sections = document.querySelectorAll('[id^="multicolumn-section-"]');
  sections.forEach((section) => {
    const sectionId = section.id.replace("multicolumn-section-", "");
    new MulticolumnSlider(sectionId);
  });
}

// Initialize on DOM ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initMulticolumnSliders);
} else {
  initMulticolumnSliders();
}

// Reinitialize on Shopify section events (theme editor)
if (typeof Shopify !== "undefined" && Shopify.designMode) {
  document.addEventListener("shopify:section:load", function (event) {
    const sectionId = event.detail.sectionId;
    new MulticolumnSlider(sectionId);
  });
}
